import { useState } from 'react';
import { Box, Button, IconButton, Popover, Typography } from '@mui/material';
import { MoreVert, Delete, Edit, Info, Download } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import styles from './MoreOptions.module.scss';
import { DocumentWithMetadataDto } from 'types/document';

type MoreOptionsProps = {
  className?: string;
  item: DocumentWithMetadataDto;
  onDownload: (doc: DocumentWithMetadataDto) => void;
  onRename?: (doc: DocumentWithMetadataDto) => void;
  onFileId: (doc: DocumentWithMetadataDto) => void;
  onDelete?: (doc: DocumentWithMetadataDto) => void;

  showRename?: boolean;
  showDelete?: boolean;
  showDownload?: boolean;
  showFileId?: boolean;
};

export function MoreOptions({
  className,
  item,
  onDownload,
  onRename,
  onFileId,
  onDelete,
  showRename = true,
  showDelete = true,
  showDownload = true,
  showFileId = true,
}: MoreOptionsProps) {
  const { t } = useTranslation('settings');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleOpen = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  return (
    <>
      <IconButton className={className} onClick={handleOpen}>
        <MoreVert
          sx={(theme) => ({
            color: theme.palette.mode === 'dark' ? theme.palette.text.primary : theme.palette.text.secondary,
          })}
        />
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        slotProps={{
          paper: {
            sx: {
              backgroundColor: 'background.paper',
              boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.15)',
            },
          },
        }}
      >
        <Box className={styles.popoverBox} onClick={handleClose}>
          {showDownload !== false && (
            <Button fullWidth onClick={() => onDownload(item)}>
              <Download sx={{ mr: 1 }} />
              <Typography variant='body5'>{t('documentsMoreOptions.download')}</Typography>
            </Button>
          )}

          {showRename && onRename && (
            <Button fullWidth onClick={() => onRename(item)}>
              <Edit sx={{ mr: 1 }} />
              <Typography variant='body5'>{t('documentsMoreOptions.rename')}</Typography>
            </Button>
          )}

          {showFileId !== false && (
            <Button fullWidth onClick={() => onFileId(item)}>
              <Info sx={{ mr: 1 }} />
              <Typography variant='body5'>{t('documentsMoreOptions.fileId')}</Typography>
            </Button>
          )}

          {showDelete && onDelete && (
            <Button fullWidth onClick={() => onDelete(item)}>
              <Delete sx={{ mr: 1 }} color='error' />
              <Typography variant='body5'>{t('documentsMoreOptions.delete')}</Typography>
            </Button>
          )}
        </Box>
      </Popover>
    </>
  );
}
